import React from "react";
import {
  Globe, PencilLine, Wand2, Loader2, Image as ImageIcon,
  ArrowRight, ArrowLeft, Target, Upload, Film,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { ChoiceCard, OBJECTIVES } from "./shared";

const UploadZone = ({ w }) => (
  <div data-testid="upload-section" className="mb-2">
    <Label className="text-zinc-300">Sube tus propios archivos (opcional)</Label>
    <div
      data-testid="upload-dropzone"
      onDragOver={(e) => { e.preventDefault(); w.setDragOver(true); }}
      onDragLeave={() => w.setDragOver(false)}
      onDrop={w.onDrop}
      onClick={() => document.getElementById("wizard-file-input")?.click()}
      className={`mt-2 cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${w.dragOver ? "border-purple-500 bg-purple-600/10" : "border-white/15 bg-black hover:border-purple-500/50"}`}
    >
      <Upload className="mx-auto text-purple-400 mb-2" size={24} />
      <p className="text-sm text-zinc-300 font-medium">Arrastra y suelta aquí, o haz clic para elegir</p>
      <p className="text-xs text-zinc-500 mt-1">Imágenes, fotos, videos o archivos de texto (máx 50 MB)</p>
      <input
        id="wizard-file-input"
        data-testid="file-input"
        type="file"
        multiple
        accept="image/*,video/*,text/*,.txt,.md,.csv"
        className="hidden"
        onChange={(e) => { w.handleFiles(e.target.files); e.target.value = ""; }}
      />
    </div>
    {w.uploading && <p className="text-xs text-purple-300 mt-2 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Subiendo archivos...</p>}
    {w.uploadedImages.length > 0 && <p className="text-xs text-zinc-300 mt-2 flex items-center gap-2"><ImageIcon size={14} className="text-purple-400" /> {w.uploadedImages.length} imagen(es) lista(s) para el siguiente paso</p>}
    {w.uploadedVideo && <p data-testid="uploaded-video" className="text-xs text-zinc-300 mt-2 flex items-center gap-2"><Film size={14} className="text-purple-400" /> Video listo: {w.uploadedVideo.filename}</p>}
    <p className="text-xs text-zinc-500 mt-2">{w.sourceType === "url" ? "Complementa lo extraído de la web con tus propias fotos, videos o textos. " : ""}Los textos se añaden a tu información. Las imágenes quedan disponibles para usarlas en el siguiente paso (con tu leyenda). El video se adjuntará a la campaña.</p>
  </div>
);

const ObjectiveBox = ({ objective, setObjective }) => (
  <div data-testid="objective-box" className="rounded-xl border border-purple-500/30 bg-purple-600/5 p-5 mt-5 mb-2">
    <div className="flex items-center gap-2 mb-1 text-purple-300">
      <Target size={18} />
      <span className="font-semibold">¿Cuál es el objetivo de esta campaña?</span>
    </div>
    <p className="text-xs text-zinc-400 mb-4">Con este dato la IA crea mejor tus textos, imágenes, video y la charla que califica a tus prospectos.</p>
    <div className="grid sm:grid-cols-2 gap-3">
      {OBJECTIVES.map((o, i) => (
        <button
          key={o.id}
          type="button"
          data-testid={`objective-${o.id}`}
          onClick={() => setObjective(o.id)}
          className={`text-left rounded-xl border px-4 py-3 transition-all ${objective === o.id ? "border-purple-500 bg-purple-600/15" : "border-white/15 bg-black hover:border-purple-500/50"}`}
        >
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full border border-white/15 flex items-center justify-center text-xs font-bold shrink-0">{String.fromCharCode(97 + i)}</span>
            <span className="font-semibold text-sm text-zinc-100">{o.label}</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1 pl-8">{o.desc}</p>
        </button>
      ))}
    </div>
  </div>
);

export const StepInfo = ({ w }) => (
  <div>
    <h2 className="font-display font-bold text-2xl mb-1">2. Información de tu campaña</h2>
    <p className="text-zinc-400 text-sm mb-6">¿De dónde sacamos la información de lo que quieres promocionar?</p>

    {w.isRenalife && (
      <div data-testid="renalife-ready-note" className="mb-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm text-emerald-200">
        <span className="font-bold text-[#FACC15]">Materiales RENALIFE listos.</span> Tu información oficial (productos, precios y marca) ya está precargada.
        No necesitas editar nada: elige el objetivo de tu campaña y presiona "Generar contenido".
      </div>
    )}

    <div className="mb-6">
      <Label className="text-zinc-300">Nombre de la campaña</Label>
      <Input data-testid="campaign-title" value={w.title} onChange={(e) => w.setTitle(e.target.value)} placeholder="Ej. Lanzamiento suplementos" className="mt-2 bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500" />
    </div>

    <div className="grid sm:grid-cols-2 gap-4 mb-6">
      <ChoiceCard testid="source-url" active={w.sourceType === "url"} onClick={() => w.setSourceType("url")} icon={Globe} title="Obtener de una página web" desc="La IA lee la página y extrae los productos/servicios por ti." />
      <ChoiceCard testid="source-manual" active={w.sourceType === "manual"} onClick={() => w.setSourceType("manual")} icon={PencilLine} title="Editar la información" desc="Tú le explicas a la IA de qué trata tu campaña." />
    </div>

    {w.sourceType === "url" && (
      <div className="rounded-xl border border-white/10 bg-[#121212] p-5 mb-5">
        <Label className="text-zinc-300">Pega la página que tiene la información</Label>
        <div className="flex flex-col sm:flex-row gap-3 mt-2">
          <Input data-testid="url-input" value={w.url} onChange={(e) => w.setUrl(e.target.value)} placeholder="https://www.miempresa.com/productos" className="bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500" />
          <Button data-testid="extract-button" onClick={w.extract} disabled={w.extracting} className="bg-purple-600 hover:bg-purple-500 rounded-xl px-6 whitespace-nowrap">
            {w.extracting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 size={16} className="mr-2" />} Extraer
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">La IA extraerá los productos o servicios publicados. Si la página no se puede leer, usa la opción "Editar la información".</p>
      </div>
    )}

    {w.sourceType === "manual" && (
      <p className="text-xs text-zinc-500 mb-2">Escribe con tus palabras qué quieres promocionar: productos, beneficios, a quién va dirigido y cualquier detalle importante. Entre más claro, mejor será tu campaña.</p>
    )}

    {(w.sourceType === "manual" || w.infoText) && (
      <div className="space-y-4 mb-2">
        <div>
          <Label className="text-zinc-300">Información de lo que quieres promocionar</Label>
          <Textarea data-testid="info-text" value={w.infoText} onChange={(e) => w.setInfoText(e.target.value)} rows={6} placeholder="Ej. Vendo suplementos naturales para energía y control de peso, dirigidos a personas ocupadas que quieren sentirse mejor..." className="mt-2 bg-black border-white/15 rounded-xl focus-visible:ring-purple-500" />
        </div>
        <div>
          <Label className="text-zinc-300">Marca comercial (opcional)</Label>
          <Input data-testid="brand-input" value={w.brand} onChange={(e) => w.setBrand(e.target.value)} placeholder="Ej. VidaPlena" className="mt-2 bg-black border-white/15 rounded-xl py-5 focus-visible:ring-purple-500" />
          <p className="text-xs text-zinc-500 mt-1">Si usas una marca, la integraremos en tus diseños. En el siguiente paso podrás elegir si añadir la leyenda "DISTRIBUIDOR INDEPENDIENTE".</p>
        </div>
      </div>
    )}

    {w.sourceType && <UploadZone w={w} />}

    {(w.sourceType === "manual" || w.infoText) && <ObjectiveBox objective={w.objective} setObjective={w.setObjective} />}

    <div className="flex justify-between mt-8">
      <Button variant="ghost" onClick={w.onBack} className="text-zinc-400"><ArrowLeft size={16} className="mr-2" /> Atrás</Button>
      <Button data-testid="next-2" disabled={w.creating || !w.infoText.trim() || !w.objective} onClick={w.createCampaign} className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl px-6 py-5">
        {w.creating ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Generar contenido <ArrowRight size={16} className="ml-2" />
      </Button>
    </div>
  </div>
);
